import { z } from 'zod';
import { IntegrationProviderSchema } from '../integrations/config.js';
// External-identity onboarding — the invite a manager issues against a user so that
// person can bind their chat account (ADR-004; the handshake is written up in
// `ems-app/knowledge/DATA-CONTRACTS.md` §Live contract — Telegram bot).
//
// Its own file rather than users/{requests,responses}: the linked-account concept is
// provider-shaped, not user-shaped, and it is consumed by both the users surface and
// (later) the self-service profile hub.
export const ExternalIdentityParamsSchema = z.object({
    projectId: z.string().trim().min(1).max(16),
    userId: z.string().trim().min(1).max(16),
    provider: IntegrationProviderSchema,
});
/** Where a person stands with a provider, as any manage-surface renders it.
 *
 *  FOUR states, and the middle two are the reason this isn't a boolean: an invite
 *  that was issued and not yet accepted is neither connected nor absent, an invite
 *  that LAPSED stays visible until re-sent (the admin must see who to chase), and
 *  the admin's next action differs in every case (invite · re-send/revoke ·
 *  re-send · remove). */
export const ExternalIdentityStateSchema = z.enum(['not_connected', 'pending', 'invite_expired', 'linked']);
export const ExternalIdentitySchema = z
    .object({
    provider: IntegrationProviderSchema,
    state: ExternalIdentityStateSchema,
    /** `@handle` when linked — what every surface shows. Never the raw numeric id:
     *  nobody recognises `7100200300`, and the handle is also what appears in the
     *  invite link. Null when the account has no username, or when not linked. */
    handle: z.string().max(64).nullable(),
    /** The provider-side profile name. Carried BECAUSE a Telegram @username is
     *  optional and plenty of real accounts have none — without this the surface
     *  would have nothing to render but a numeric id, which names nobody. */
    name: z.string().max(128).nullable(),
    linked_at: z.string().datetime().nullable(),
    /** Pending + invite_expired — so the surface can say "expires in 5 days" on a
     *  live invite and "expired on …" on a lapsed one, rather than making the
     *  admin guess whether a week-old invite is still good. */
    expires_at: z.string().datetime().nullable(),
    /** PENDING only — the outstanding invite's credentials, re-readable until it
     *  is used, replaced or lapses. This is the whole reason they are stored in
     *  plaintext (SECURITY §Secrets): the admin who could mint them can re-copy
     *  them, instead of re-issuing and killing the link the person is holding.
     *  `deep_link` is null when THIS project has no bot to address (the code
     *  still redeems through any project's bot); both null outside 'pending' —
     *  an expired credential is unusable, so it is never re-shown. */
    deep_link: z.string().max(512).nullable(),
    short_code: z.string().max(16).nullable(),
    /** Whether THIS project's bot can message them. Distinct from `state`: identity
     *  is global, reachability is per-bot, and a person linked through another
     *  project's bot is `linked` here yet unreachable until they open this bot's
     *  chat. `null` = the project has no bot configured, so the question is moot. */
    reachable_here: z.boolean().nullable(),
    /** THIS project's bot handle (null = no bot configured). Carried so the
     *  surface can hand over the bot's ADDRESS — `https://t.me/<bot_username>` —
     *  the rejoin path for a linked-but-unreachable person (no credential needed:
     *  identity is global, and their first message heals reachability). */
    bot_username: z.string().max(32).nullable(),
})
    .strict();
/** What an issued invite hands back. Shown ONCE per issue in the sense that the
 *  admin acts on it immediately — but re-readable while pending (that is the whole
 *  reason the credentials are not hashed; see SECURITY §Secrets). */
export const ExternalIdentityInviteSchema = z
    .object({
    /** The one-tap link: `https://t.me/<bot_username>?start=<token>`. Per (person,
     *  project) — the token is the person, the bot username is the project's bot. */
    deep_link: z.string().max(512),
    /** The same invite, as 8 Crockford-base32 chars formatted `XXXX-XXXX`, for the
     *  person who finds the bot unaided or is read the code over the phone. */
    short_code: z.string().max(16),
    expires_at: z.string().datetime(),
    /** The bot the link points at, so the surface can name it ("they'll be talking
     *  to @ems_companion_bot") instead of handing over an opaque URL. */
    bot_username: z.string().max(32),
})
    .strict();
/** The result of an admin-triggered test message.
 *
 *  REPORTS rather than throws (like the integration connection check): "we couldn't
 *  reach them" is the answer, not a failed request. It is also the authoritative
 *  reachability probe — `reachable_here` is a cached belief, and a send is the only
 *  thing that actually proves it — so the response carries the corrected value and
 *  the server persists it. */
export const TestMessageResponseSchema = z
    .object({
    delivered: z.boolean(),
    /** Present when delivery failed — a CODE the client localizes, never provider prose. */
    reason: z
        .enum(['not_reachable', 'no_bot', 'not_linked', 'provider_error', 'provider_unreachable'])
        .nullable(),
    /** The corrected reachability, so the surface updates from the truth rather than
     *  from what it believed a moment ago. */
    reachable_here: z.boolean(),
})
    .strict();
